import { useContext } from 'react';
import styled, { css, ThemeContext } from 'styled-components';

import { FontWeight } from 'design-system/variables/text';
import Checkmark from 'images/inquiry/animations/checkmark.json';
import LoadingGeneric from 'images/inquiry/animations/loading-generic.json';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import ThemedLottie from 'inquiry/components/ThemedLottie';
import useIsMounted from 'lib/hooks/useIsMounted';
import useToggle from 'lib/hooks/useToggle';
import { colorCss } from 'lib/inquiryStyles';
import { SerializedStepSelfieStyles, StepSelfieConfig } from 'lib/interfaces/StepsConfig';
import { captureException, captureMessage } from 'lib/reporting';
import { SelfieStep } from 'verify/lib/interfaces/selfie';
import DirectionalHint from 'verify/scanners/SelfieScanner/DirectionalHint';
import LoadingCircle from 'verify/scanners/SelfieScanner/LoadingCircle';

const Container = styled('div')`
  position: relative;
  height: 100%;
`;

const OverlayCrop = styled('div')`
  position: relative;
  height: 100%;
  width: 100%;
  overflow-y: hidden;
`;

interface OverlayProps {
  isComplete: boolean;
  isNarrowed: boolean;
  profileSize: number;
  fadeOutOnComplete: boolean;
  styles?: SerializedStepSelfieStyles;
}

const Overlay = styled('div')<OverlayProps>`
  position: absolute;
  /* NB: Very large spreads (~800px) don't render properly in safari */
  box-shadow: 0 0 1px 750px
    ${({ styles, theme }) => colorCss(styles?.base?.backgroundColor) ?? theme.colorModalBackground};
  height: 100%;
  width: 100%;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  margin-left: -1px;
  margin-right: -1px;

  background-color: rgba(255, 255, 255, 0);
  transition: 0.7s;

  ${({ isNarrowed, profileSize }) =>
    isNarrowed &&
    // NB: Safari fails to render border-radius >= 50%
    css`
      border-radius: 49.9%;
      height: ${profileSize}px;
      width: ${profileSize}px;
    `};

  ${({ isComplete, fadeOutOnComplete, styles, theme }) =>
    // Only block out screen on completion if there is a custom asset, to avoid
    // the custom asset overlapping with focus ring
    isComplete &&
    fadeOutOnComplete &&
    css`
      background-color: ${colorCss(styles?.base?.backgroundColor) ?? theme.colorModalBackground};
    `};
`;

const HudPositioner = styled('div')<{
  isVisible: boolean;
  constrainWidth?: boolean;
}>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity 1s ease-in-out;
  /**
   * Used to constrain the size of custom assets to avoid resource usage overload
   */
  ${({ constrainWidth }) => constrainWidth && 'max-width: 100%;'}
`;

interface ProgressOverlayProps {
  assets?: StepSelfieConfig['assets']['record-page'];
  currentStep?: SelfieStep;
  poseOrder: SelfieStep[];
  isNarrowed: boolean;
  showDelayedHint: boolean;
  profileSize: number;
  styles?: SerializedStepSelfieStyles;
  uploadProgress?: number;
  showLoadingCircle?: boolean;
  enableMobilePendingExperienceForWeb: boolean;
}

function calculatePercentage(currentStep: SelfieStep | undefined, poseOrder: SelfieStep[]): number {
  let percent = 100;
  const stepIndex = poseOrder.findIndex((pose) => pose === currentStep);
  if (currentStep == null) {
    percent = 100;
  } else if (stepIndex === 0 || stepIndex === 1) {
    percent = 0;
  } else if (stepIndex === 2) {
    percent = 50;
  } else {
    captureException(
      new TypeError(`currentStep is unrecognizable type: ${JSON.stringify(currentStep)}`),
    );
  }

  return percent;
}

export default function ProgressOverlay({
  assets,
  showDelayedHint,
  isNarrowed,
  profileSize,
  currentStep,
  poseOrder,
  styles,
  uploadProgress,
  showLoadingCircle,
  enableMobilePendingExperienceForWeb,
}: ProgressOverlayProps) {
  const theme = useContext(ThemeContext);

  const percent = showLoadingCircle ? calculatePercentage(currentStep, poseOrder) : 100;

  // This value is defined as a border that only supports top
  // Because the web UI is larger than mobile, we magnify the server-sent ring width to look better.
  const strokeWidth = (styles?.captureFeedbox?.border?.width?.top?.value ?? 5) * 2;
  // Value is eyeballed to get the circle to go fully over the camera circle
  // As strokeWidth goes down does this need to go up or down and by how much
  const width = profileSize - 2 + strokeWidth * 4;
  const isComplete = currentStep == null;

  return (
    <Container>
      <OverlayCrop>
        <Overlay
          isComplete={isComplete}
          isNarrowed={isNarrowed}
          profileSize={profileSize}
          styles={styles}
          fadeOutOnComplete={
            assets?.['loading-pictograph'] != null || enableMobilePendingExperienceForWeb
          }
        />
        {showLoadingCircle && (
          <HudPositioner isVisible={!isComplete}>
            <LoadingCircle
              width={width}
              strokeWidth={strokeWidth}
              percent={percent}
              color={colorCss(styles?.captureFeedbox?.border?.color) ?? theme.colorPrimary}
            />
          </HudPositioner>
        )}
        <HudPositioner isVisible={isComplete} constrainWidth>
          {isComplete && !enableMobilePendingExperienceForWeb && (
            // Conditionally render animation so it starts playing only when ring appears
            <CustomAssetWithFallback
              customImage={assets?.['loading-pictograph']}
              alt='loading pictograph'
            >
              <CheckmarkAnimation
                strokeColor={
                  colorCss(styles?.captureCompleteIcon?.stroke.color) ?? theme.colorPrimary
                }
                uploadProgress={uploadProgress}
              />
            </CustomAssetWithFallback>
          )}
        </HudPositioner>
      </OverlayCrop>

      <DirectionalHint
        assets={assets}
        isActive={showDelayedHint}
        profileSize={profileSize}
        currentStep={currentStep}
        styles={styles?.captureIcon}
      />
    </Container>
  );
}

const ProgressNumber = styled.span<{ color: string }>`
  color: ${({ color }) => color};
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  font-weight: ${FontWeight.Medium};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LoadingPositioner = styled.div`
  position: relative;
`;

interface CheckmarkAnimationProps {
  strokeColor?: string;
  uploadProgress?: number;
}

const CHECKMARK_ANIMATION_DURATION_MS = 3000;
const SHOW_UPLOAD_PROGRESS_DELAY_MS = 10000;

const CheckmarkAnimation = ({ strokeColor, uploadProgress }: CheckmarkAnimationProps) => {
  const [showSpinner, { enable: enableSpinner }] = useToggle(false);
  const [showUploadProgress, { enable: enableUploadProgress }] = useToggle(false);
  const isMounted = useIsMounted();
  const handleCheckmarkAnimationComplete = () => {
    setTimeout(() => {
      if (!isMounted) return;
      enableSpinner();
    }, CHECKMARK_ANIMATION_DURATION_MS);

    // Only show progress number after 10s if spinner is still going
    setTimeout(() => {
      if (!isMounted) return;
      enableUploadProgress();
    }, SHOW_UPLOAD_PROGRESS_DELAY_MS);
  };

  if (strokeColor == null) {
    captureMessage(`unexpected ${strokeColor} strokeColor`, 'warning');
  }

  return (
    <LoadingPositioner>
      <ThemedLottie
        aria-label='complete checkmark'
        aria-busy
        aria-live='polite'
        role='alert'
        animationData={showSpinner ? LoadingGeneric : Checkmark}
        onComplete={handleCheckmarkAnimationComplete}
        loop={showSpinner}
        $strokeColor={strokeColor}
      />
      {showUploadProgress && uploadProgress != null && uploadProgress > 0 && (
        <ProgressNumber color={strokeColor as any}>
          {Math.round(uploadProgress * 100)}%
        </ProgressNumber>
      )}
    </LoadingPositioner>
  );
};
